import React, { useMemo, useState } from "react";
import { useGetTransactionsQuery } from "actions/transactions/queries";
import { useReconstructionComptableMutation } from "actions/comptabilite/mutations";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import HeaderFirst from "utils/HeaderFirst";
import { toastApiErrors } from "utils/toastApiError";
import { useDossierRole } from "contexts/DossierRoleContext";

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getFileNameFromPath = (path) => {
  if (!path || typeof path !== "string") return "-";
  const normalized = path.replace(/\\/g, "/");
  const parts = normalized.split("/");
  return parts[parts.length - 1] || "-";
};

/** Tri : date d'opération (date_transaction) puis dates de secours - pas la seule date de création si opération connue */
const operationTimestamp = (item) => {
  const candidates = [item?.date_transaction, item?.date_operation, item?.created_at, item?.updated_at];
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const t = new Date(value).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return 0;
};

const AssistantIAPage = () => {
  const { canEdit } = useDossierRole();
  const { clientID: dossierId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isReconstructionModalOpen, setIsReconstructionModalOpen] = useState(false);
  const [isReconstructing, setIsReconstructing] = useState(false);
  const [reconstructionResult, setReconstructionResult] = useState(null);
  const PAGE_SIZE = 8;
  const [reconstructionComptable] = useReconstructionComptableMutation();
  const { data: transactionsData, refetch: refetchTransactions } = useGetTransactionsQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );

  const handleReconstructionConfirm = async () => {
    if (!dossierId) return;
    try {
      setIsReconstructing(true);
      const response = await reconstructionComptable({
        dossier_id: Number(dossierId),
        ocr_only: true,
      }).unwrap();
      const result = response?.resultat || {};
      setReconstructionResult(result);
      const totalCreated = Number(result.ecritures_reconstruites || 0);
      toast.success(`Reconstruction terminée: ${totalCreated.toLocaleString("fr-FR")} écriture(s) créée(s).`);
      refetchTransactions();
    } catch (error) {
      toastApiErrors(error);
    } finally {
      setIsReconstructing(false);
    }
  };

  const transactions = useMemo(() => {
    const rows = Array.isArray(transactionsData?.transactions) ? transactionsData.transactions : [];
    const withOcrAndFile = rows.filter((item) => {
      const isOcr = item?.ocr === true;
      const pj = item?.piece_joint;
      const hasFile = pj != null && (typeof pj !== "string" || pj.trim() !== "");
      return isOcr && hasFile;
    });
    return [...withOcrAndFile].sort((a, b) => operationTimestamp(b) - operationTimestamp(a));
  }, [transactionsData]);

  const filteredTransactions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((item) => {
      const fileName = getFileNameFromPath(item.piece_joint).toLowerCase();
      const tiers = (item.client_details?.nom || item.fournisseurs_details?.nom || "").toLowerCase();
      const montantPaye = String(item.montant_paye ?? "").toLowerCase();
      const montantTotal = String(item.montant_total ?? item.prix ?? "").toLowerCase();
      return (
        fileName.includes(q) ||
        tiers.includes(q) ||
        montantPaye.includes(q) ||
        montantTotal.includes(q)
      );
    });
  }, [transactions, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, currentPage, PAGE_SIZE]);

  const firstItem = filteredTransactions.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastItem = Math.min(currentPage * PAGE_SIZE, filteredTransactions.length);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, transactions.length]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  //const comptesDetail = transactionsData?.comptes_detail || {};

  return (
    <div className="assistant-ia-page-root" style={{ backgroundColor: "white", borderRadius: 16, padding: 16, minHeight: 460 }}>
      <style>
        {`
          .assistant-ia-page-root {
            margin-bottom: 14px;
          }
          .assistant-ia-action-btn {
            border-radius: 24px;
            transition: box-shadow 0.2s ease, transform 0.2s ease;
          }
          .assistant-ia-btn-green,
          .assistant-ia-btn-green.btn,
          .assistant-ia-btn-green.btn-primary {
            --bs-btn-bg: #009688;
            --bs-btn-border-color: #009688;
            --bs-btn-hover-bg: #00796b;
            --bs-btn-hover-border-color: #00796b;
            --bs-btn-active-bg: #00695c;
            --bs-btn-active-border-color: #00695c;
            --bs-btn-disabled-bg: #009688;
            --bs-btn-disabled-border-color: #009688;
            background-color: #009688 !important;
            border-color: #009688 !important;
            color: #fff !important;
          }
          .assistant-ia-btn-green:hover,
          .assistant-ia-btn-green.btn:hover,
          .assistant-ia-btn-green.btn-primary:hover,
          .assistant-ia-btn-green:focus,
          .assistant-ia-btn-green.btn:focus,
          .assistant-ia-btn-green.btn-primary:focus {
            background-color: #00796b !important;
            border-color: #00796b !important;
            color: #fff !important;
          }
          .assistant-ia-action-btn:hover {
            box-shadow: 0 8px 22px rgba(0, 0, 0, 0.16);
            transform: translateY(-1px);
          }
          .assistant-ia-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1050;
            padding: 16px;
          }
          .assistant-ia-modal-card {
            width: 100%;
            max-width: 560px;
            background: #fff;
            border-radius: 12px;
            box-shadow: 0 16px 42px rgba(0, 0, 0, 0.24);
            padding: 18px;
          }
          .assistant-ia-table-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 10px;
            flex-wrap: wrap;
          }
          .assistant-ia-search-input {
            border: 1px solid #d0d5dd;
            border-radius: 10px;
            padding: 8px 10px;
            min-width: 260px;
            max-width: 100%;
            font-size: 13px;
            outline: none;
          }
          .assistant-ia-search-input:focus {
            border-color: #009688;
            box-shadow: 0 0 0 3px rgba(0, 150, 136, 0.15);
          }
          .assistant-ia-pagination {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 10px;
          }
          .assistant-ia-pagination-controls {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .assistant-ia-page-btn {
            border: 1px solid #cdd5df;
            background: white;
            color: #344054;
            border-radius: 8px;
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 600;
          }
          .assistant-ia-page-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          @media (max-width: 768px) {
            .assistant-ia-page-root {
              margin-bottom: calc(62px + env(safe-area-inset-bottom, 0px) + 16px);
            }
            .assistant-ia-search-input {
              min-width: 100%;
              width: 100%;
            }
          }
        `}
      </style>
      <HeaderFirst
        nom={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h4 className="titre-dossier" style={{ margin: 0 }}>
              Assistant IA Comptable
            </h4>
            {canEdit && (
              <button
                type="button"
                className="btn assistant-ia-btn-green assistant-ia-action-btn"
                onClick={() => navigate(`/client/${dossierId}/assistant-ia/operation/`)}
              >
                Effectuer une opération
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary assistant-ia-action-btn"
              onClick={() => {
                setReconstructionResult(null);
                setIsReconstructionModalOpen(true);
              }}
              disabled={isReconstructing || !canEdit}
            >
              {isReconstructing ? "Reconstruction..." : "Reconstruction comptable"}
            </button>
          </div>
        }
      />

      <div style={{ marginTop: 16, background: "#f8f9fa", borderRadius: 10, padding: 12 }}>
        <strong>Flux Assistant IA</strong>
        <p style={{ margin: "6px 0 0", color: "#6c757d" }}>
          1) Upload facture OCR, 2) enrichissement comptable assisté IA, 3) validation et soumission d&apos;une entrée/sortie.
        </p>
      </div>

      <div style={{ marginTop: 20 }}>
        <h6>Transactions réelles du dossier (persistées)</h6>
        <div className="assistant-ia-table-toolbar">
          <input
            type="text"
            className="assistant-ia-search-input"
            placeholder="Rechercher (fichier, tiers, montant...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span style={{ color: "#667085", fontSize: 12 }}>
            {filteredTransactions.length} résultat(s)
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-striped table-bordered" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th>Nom du fichier</th>
                <th>Tiers</th>
                <th>Montant payé</th>
                <th>Montant total transaction</th>
                <th>Justificatif</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#6c757d" }}>
                    Aucune transaction trouvée.
                  </td>
                </tr>
              )}
              {paginatedTransactions.map((item, idx) => (
                <tr key={`persisted-${item.type}-${item.id || idx}-${item.created_at || idx}`}>
                  <td>{getFileNameFromPath(item.piece_joint) !== "-" ? getFileNameFromPath(item.piece_joint) : "Facture OCR"}</td>
                  <td>{item.client_details?.nom || item.fournisseurs_details?.nom || "-"}</td>
                  <td>{formatAmount(item.montant_paye || 0)}</td>
                  <td>{formatAmount(item.montant_total || item.prix)}</td>
                  <td>
                    {item.justificatif_file ? (
                      <a href={`${item.justificatif_file}`} target="_blank" rel="noopener noreferrer" style={{ color: "#009688", fontSize: 12 }}>
                        {getFileNameFromPath(item.justificatif_file)}
                      </a>
                    ) : (
                      <span style={{ color: "#6c757d", fontSize: 12 }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="assistant-ia-pagination">
          <span style={{ color: "#667085", fontSize: 12 }}>
            {filteredTransactions.length === 0
              ? "0 sur 0"
              : `${firstItem} - ${lastItem} sur ${filteredTransactions.length}`}
          </span>
          <div className="assistant-ia-pagination-controls">
            <button
              type="button"
              className="assistant-ia-page-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Précédent
            </button>
            <span style={{ fontSize: 12, color: "#475467", minWidth: 70, textAlign: "center" }}>
              Page {currentPage}/{totalPages}
            </span>
            <button
              type="button"
              className="assistant-ia-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {isReconstructionModalOpen && (
        <div className="assistant-ia-modal-backdrop" role="dialog" aria-modal="true">
          <div className="assistant-ia-modal-card">
            {!reconstructionResult ? (
              <>
                <h5 style={{ marginTop: 0, marginBottom: 10 }}>Confirmation de reconstruction</h5>
                <p style={{ marginBottom: 16, color: "#495057" }}>
                  Cette action va reconstruire les écritures comptables des opérations IA/OCR du dossier.
                  Voulez-vous continuer ?
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-light assistant-ia-action-btn"
                    onClick={() => setIsReconstructionModalOpen(false)}
                    disabled={isReconstructing}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn assistant-ia-btn-green assistant-ia-action-btn"
                    onClick={handleReconstructionConfirm}
                    disabled={isReconstructing}
                  >
                    {isReconstructing ? "Reconstruction..." : "Confirmer"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h5 style={{ marginTop: 0, marginBottom: 12 }}>Reconstruction terminée</h5>
                <div
                  style={{
                    border: "1px solid #e9ecef",
                    borderRadius: 10,
                    padding: 12,
                    background: "#f8f9fa",
                    marginBottom: 14,
                  }}
                >
                  <div><strong>Dossier:</strong> {reconstructionResult.dossier_id}</div>
                  <div><strong>Mode OCR uniquement:</strong> {reconstructionResult.ocr_only ? "Oui" : "Non"}</div>
                  <div><strong>Opérations entrée traitées:</strong> {reconstructionResult.operations_entree || 0}</div>
                  <div><strong>Opérations sortie traitées:</strong> {reconstructionResult.operations_sortie || 0}</div>
                  <div><strong>Écritures supprimées:</strong> {Number(reconstructionResult.ecritures_supprimees || 0).toLocaleString("fr-FR")}</div>
                  <div><strong>Écritures créées:</strong> {Number(reconstructionResult.ecritures_reconstruites || 0).toLocaleString("fr-FR")}</div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button
                    type="button"
                    className="btn btn-light assistant-ia-action-btn"
                    onClick={() => setIsReconstructionModalOpen(false)}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary assistant-ia-action-btn"
                    onClick={() => setReconstructionResult(null)}
                  >
                    Relancer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantIAPage;
